import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, useController } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Dot from 'components/Dot';
import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Dropdown } from 'components/HookForm';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import MobileActionSheet from 'components/MobileActionSheet';
import {
  ITEM_AVAILABILITY_STATUS,
  ITEM_AVAILABILITY_STATUS_MAPPING,
  ITEM_AVAILABILITY_STATUS_OPTIONS,
} from '../SkuItems.constants';
import { skuItemAvailabilityStatusMessages as messages } from '../SkuItems.messages';
import { sortSelectedFirst } from '../SkuMenuOverview.helpers';

import './SkuEditItemAvailabilityDropdown.scss';

const { block, element } = bem('sku-edit-item-availability');

const StatusMarker = ({ className, status }) => {
  if (status === ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY) {
    return <Isvg className={className} src={ICONS.noSymbol} />;
  }
  return <Dot className={className} color={ITEM_AVAILABILITY_STATUS_MAPPING[status]?.color} largeSize />;
};

StatusMarker.propTypes = {
  className: PropTypes.string,
  status: PropTypes.oneOf(ITEM_AVAILABILITY_STATUS_OPTIONS),
};

const SkuEditItemAvailabilityDropdown = ({ name, isPending = false }) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { control } = useFormContext();
  const {
    field: { value: availabilityStatus, onChange },
  } = useController({ name, control });

  if (isMobile) {
    const statusOptions = isPending
      ? []
      : ITEM_AVAILABILITY_STATUS_OPTIONS.map(option => ({
          label: (
            <>
              <StatusMarker className={element('dropdown-status-marker')} status={option} />
              {intl.formatMessage(messages[ITEM_AVAILABILITY_STATUS_MAPPING[option]?.message])}
            </>
          ),
          onClick: () => {
            if (option !== availabilityStatus) {
              onChange(option);
            }
          },
        }));

    return (
      <div className={block()}>
        <button
          type="button"
          className={element('mobile-toggle', { pending: isPending })}
          data-testid="sku-edit-item-availability-toggle"
          onClick={() => setIsSheetOpen(true)}
          disabled={isPending}>
          <span className={element('marker')}>
            {isPending ? <Isvg src={ICONS.clock} /> : <StatusMarker status={availabilityStatus} />}
          </span>
          <span className={element('label')}>
            {isPending
              ? intl.formatMessage(messages.itemStatusPending)
              : intl.formatMessage(messages[ITEM_AVAILABILITY_STATUS_MAPPING[availabilityStatus]?.message])}
          </span>
          {!isPending && (
            <span className={element('caret')}>
              <SvgArrow direction="down" />
            </span>
          )}
        </button>
        <MobileActionSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} options={statusOptions} />
      </div>
    );
  }

  return (
    <div className={block()}>
      <Dropdown
        className={element('dropdown')}
        name={name}
        disabled={isPending}
        isAlignTopToggleStyle
        isLargeRadiusStyle
        showMenuCaret>
        {({ closeMenu, onChange: dropdownOnChange, value: dropdownValue }) => (
          <>
            <DropdownToggle isRoundRadiusStyle>
              <When predicate={isPending}>
                <Isvg className={element('status-marker')} src={ICONS.clock} />
                <Message message={messages.itemStatusPending} />
              </When>
              <When predicate={!isPending}>
                <StatusMarker className={element('status-marker')} status={dropdownValue} />
                <Message message={messages[ITEM_AVAILABILITY_STATUS_MAPPING[dropdownValue]?.message]} />
              </When>
            </DropdownToggle>
            <DropdownMenu className={element('dropdown-menu')}>
              {!isPending &&
                sortSelectedFirst(ITEM_AVAILABILITY_STATUS_OPTIONS, dropdownValue).map(status => {
                  const { message } = ITEM_AVAILABILITY_STATUS_MAPPING[status];
                  return (
                    <DropdownItem
                      key={status}
                      isSelected={dropdownValue === status}
                      onClick={() => {
                        dropdownOnChange(status);
                        closeMenu();
                      }}>
                      <StatusMarker className={element('dropdown-status-marker')} status={status} />
                      <Message message={messages[message]} />
                    </DropdownItem>
                  );
                })}
            </DropdownMenu>
          </>
        )}
      </Dropdown>
    </div>
  );
};

SkuEditItemAvailabilityDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  isPending: PropTypes.bool,
};

export default SkuEditItemAvailabilityDropdown;
